import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	Chip,
	Checkbox,
	FormControlLabel,
	CircularProgress,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { departments, custom } from "data/authData";
import { allowAccess } from "utils/userAccessFun";

const ViewEditRemarkModal = ({ modal, setModal, styleData, onSubmitSuccess, disableEdit = false }) => {
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [remarkData, setRemarkData] = useState(null);
	const [remark, setRemark] = useState("");
	const [selectedDepartments, setSelectedDepartments] = useState([]);
	const [fetchError, setFetchError] = useState(false);

	const hasEditAccess = useMemo(
		() => (disableEdit ? false : allowAccess(custom.STYLE_REVIEWER.key, user, departments.MDM.key)),
		[user],
	);
	// const hasEditAccess = false;

	const departmentOptions = useMemo(
		() => Object.values(departments).filter((d) => d.key === departments.DESIGNER.key || d.key === departments.SOURCING.key),
		[],
	);

	useEffect(() => {
		if (modal && styleData?.remark_id) {
			setFetchError(false);
			fetchRemarkDetails();
		}
	}, [modal, styleData]);

	const fetchRemarkDetails = async () => {
		try {
			const { data } = await axios.get(`/api/designer/style_remark/${styleData.remark_id}`);
			const remarkResult = data.result;
			setRemarkData(remarkResult);
			setRemark(remarkResult.remark || "");
			const depts = remarkResult.departments?.map((key) => departments[key]).filter(Boolean) || [];
			setSelectedDepartments(depts);
			setFetchError(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
			setFetchError(true);
		}
	};

	const usersToNotify = useMemo(() => {
		const isValidEmail = (value) => {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return emailRegex.test(value);
		};
		const userSet = new Set();

		const selectedDeptKeys = selectedDepartments.map((d) => d.key);

		if (selectedDeptKeys.includes(departments.SOURCING.key)) {
			if (styleData?.fabric_updated_by && isValidEmail(styleData.fabric_updated_by)) {
				userSet.add(styleData.fabric_updated_by);
			}
		}

		if (selectedDeptKeys.includes(departments.DESIGNER.key)) {
			if (styleData?.created_by && isValidEmail(styleData.created_by)) {
				userSet.add(styleData.created_by);
			}
		}

		if (user?.email) userSet.add(user.email);
		return Array.from(userSet);
	}, [styleData, user, selectedDepartments]);

	const handleDepartmentToggle = (dept) => {
		if (!hasEditAccess) return;
		setSelectedDepartments((prev) => {
			const exists = prev.find((d) => d.key === dept.key);
			return exists ? prev.filter((d) => d.key !== dept.key) : [...prev, dept];
		});
	};

	const handleClose = () => {
		setModal(false);
		setRemarkData(null);
		setRemark("");
		setSelectedDepartments([]);
		setFetchError(false);
	};

	const handleSubmit = async () => {
		if (!remark.trim()) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "warning", message: "Please enter a remark" });
			return;
		}

		setLoadingOpen(true);
		try {
			const { data } = await axios.put(`/api/designer/style_remark/${styleData.remark_id}`, {
				remark,
				users_to_notify: usersToNotify,
				departments: selectedDepartments.map((d) => d.key),
			});
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "success", message: "Remark updated successfully" });
			setLoadingOpen(false);
			handleClose();
			if (onSubmitSuccess) onSubmitSuccess(data);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, severity: "error", message: parseErrorMessage(error) });
			setLoadingOpen(false);
		}
	};

	return (
		<Dialog open={modal} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{hasEditAccess ? "Edit Remark" : "View Remark"}</DialogTitle>
			<DialogContent>
				{!remarkData && !fetchError ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
						<CircularProgress />
					</Box>
				) : fetchError ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
						<Typography color="error">Failed to load remark details. Please try again.</Typography>
					</Box>
				) : (
					<>
						<Box mb={2}>
							<Typography variant="subtitle2" color={grey[700]} mb={1}>
								Style ID:
							</Typography>
							<Chip label={styleData?.style_id} size="small" />
						</Box>

						<Box mb={2}>
							<Typography variant="subtitle2" color={grey[700]} mb={1}>
								Select Departments to be Notified:
							</Typography>
							<Box display="flex" gap={0.5}>
								{departmentOptions.map((dept) => (
									<FormControlLabel
										key={dept.key}
										control={
											<Checkbox
												checked={selectedDepartments.some((d) => d.key === dept.key)}
												onChange={() => handleDepartmentToggle(dept)}
												size="small"
												disabled={!hasEditAccess}
											/>
										}
										label={<Typography fontSize={14}>{dept.name}</Typography>}
									/>
								))}
							</Box>
						</Box>

						<Box mb={2}>
							<Typography variant="subtitle2" color={grey[700]} mb={1}>
								Users to be Notified:
							</Typography>
							<Box display="flex" flexWrap="wrap" gap={0.5}>
								{usersToNotify.map((username) => (
									<Chip key={username} label={username} size="small" color="primary" variant="outlined" />
								))}
							</Box>
						</Box>

						<TextField
							label="Remark"
							multiline
							rows={4}
							fullWidth
							value={remark}
							onChange={(e) => setRemark(e.target.value)}
							placeholder="Enter your remark here..."
							disabled={!hasEditAccess}
							InputProps={{
								readOnly: !hasEditAccess,
							}}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Close</Button>
				{hasEditAccess && remarkData && !fetchError && (
					<Button onClick={handleSubmit} variant="contained">
						Update
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default ViewEditRemarkModal;
